import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/swiper-bundle.css';
import { Navigation, Pagination, Mousewheel, Keyboard } from 'swiper/modules';
import SectionHeader from '../../home/components/SectionHeader';
import Icon from '../../common/components/Icon';
import { useTranslations } from '../../localization/useTranslations';
import AuthorAndTopicCard from '../../newsletter/components/AuthorAndTopicCard';
import {
  getNewslettersByIdArray,
  getUserNewsLetterCategories,
  subscribeToAuthorOrTopic,
  unsubscribeFromAuthorOrTopic,
} from '../../newsletter/services/NewsletterService';
import { Author, NewsletterResponse } from '../../newsletter/types/Newsletter';
import { handleUserActionWithLoginCheck } from '../../common/LoggedOutUserActions';
import useScrollbackAfterRedirect from '../../hooks/useScrollbackAfterRedirect';

interface NewsletterCarouselProps {
  cardType: 'author' | 'topic';
  openSnackbar: (message: string) => void;
}

const NewsletterCarousel: React.FC<NewsletterCarouselProps> = ({ cardType, openSnackbar }) => {
  useScrollbackAfterRedirect(); // Restore scroll position if redirected from login
  const [nonSubscribedItems, setNonSubscribedItems] = useState<Author[] | null>(null); // Holds non-subscribed authors or topics
  const [subscribedItems, setSubscribedItems] = useState<Author[] | null>(null); // Holds subscribed authors or topics
  const [allItems, setAllItems] = useState<Author[] | null>(null); // Holds all authors or topics
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const translations = useTranslations();

  // Function to get snackbar messages using translations object
  const getSnackbarMessage = (
    action: 'subscribe' | 'unsubscribe' | 'failedSubscribe' | 'failedUnsubscribe'
  ): string => {
    if (cardType === 'author') {
      switch (action) {
        case 'subscribe':
          return translations?.snackBarMessages?.subscribedAuthor || 'Subscribed to author';
        case 'unsubscribe':
          return translations?.snackBarMessages?.unsubscribedAuthor || 'Unsubscribed from author';
        case 'failedSubscribe':
          return translations?.snackBarMessages?.failedSubscribeAuthor || 'Failed to subscribe to author';
        case 'failedUnsubscribe':
          return translations?.snackBarMessages?.failedUnsubscribeAuthor || 'Failed to unsubscribe from author';
        default:
          return '';
      }
    } else if (cardType === 'topic') {
      switch (action) {
        case 'subscribe':
          return translations?.snackBarMessages?.subscribedTopic || 'Subscribed to topic';
        case 'unsubscribe':
          return translations?.snackBarMessages?.unsubscribedTopic || 'Unsubscribed from topic';
        case 'failedSubscribe':
          return translations?.snackBarMessages?.failedSubscribeTopic || 'Failed to subscribe to topic';
        case 'failedUnsubscribe':
          return translations?.snackBarMessages?.failedUnsubscribeTopic || 'Failed to unsubscribe from topic';
        default:
          return '';
      }
    }

    return '';
  };

  const sortList = useCallback((items: Author[], variants: Variant[]) => {
    // Sort variants by the 'sorting' property, if available
    const sortedVariants = Array.isArray(variants) ? variants.sort((a, b) => a.sorting - b.sorting) : variants;

    const sortedItems: Author[] = [];

    sortedVariants.forEach((variant) => {
      const item = items.find((item: Author) => item.id.toString() === variant.code);

      if (item) sortedItems.push(item);
    });

    // Sort the items by the 'name' property after sorting by variants
    return sortedItems.sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const fetchData = useCallback(
    async (response: NewsletterResponse, subscriptionListArray: List[], type: 'author' | 'topic') => {
      try {
        const listCode = type === 'author' ? 'authors' : 'topics';

        const category = type === 'author' ? 'coauthor' : window.country === 'cz' ? 'stitek' : 'categories';

        const items = response.lists.find((list) => list.code === listCode)?.variants || [];

        const subscribedItems = subscriptionListArray.find((list) => list.code === listCode)?.variants || [];

        const itemsArray = Object.values(items);
        const subscribedItemsArray = Object.values(subscribedItems);

        const fetchedList = await getNewslettersByIdArray(itemsArray.map((item) => item.code).join(','), category);

        const sortedList = sortList(fetchedList, itemsArray);

        const subscribedList = sortedList.filter((item: Author) =>
          subscribedItemsArray.some((subItem) => subItem.code === item.id.toString())
        );

        const notSubscribedList = sortedList.filter(
          (item: Author) => !subscribedItemsArray.some((subItem) => subItem.code === item.id.toString())
        );

        setNonSubscribedItems(notSubscribedList);
        setSubscribedItems(subscribedList);
        setAllItems(sortedList);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [sortList]
  );

  const getAuthorsAndTopics = useCallback(async () => {
    try {
      const response = window.NewsletterLists;

      const subscriptionsList = await getUserNewsLetterCategories();

      const subscriptionListArray: List[] = Object.values(subscriptionsList.subscriptions || []);

      fetchData(response, subscriptionListArray, cardType);
    } catch (error) {
      console.error(error);
    }
  }, [fetchData, cardType]);

  useEffect(() => {
    getAuthorsAndTopics();
  }, [getAuthorsAndTopics]);

  const handleFollow = async (id: number, type: 'author' | 'topic') => {
    // Determine the type of subscription
    const subscribeType = type === 'author' ? 'authors' : 'topics';
    const item = nonSubscribedItems?.find((item) => item.id === id);

    try {
      // Subscribe to author or topic
      if (item) {
        const response = await subscribeToAuthorOrTopic(subscribeType, item.id);

        if (response.status === 'ok') {
          setNonSubscribedItems(nonSubscribedItems?.filter((item) => item.id !== id) || null);

          setSubscribedItems([...(subscribedItems || []), item]);

          openSnackbar(getSnackbarMessage('subscribe'));
        } else {
          throw new Error();
        }
      } else {
        // Unsubscribe from author or topic
        const subscribedItem = subscribedItems?.find((item) => item.id === id);

        if (!subscribedItem) return;

        const response = await unsubscribeFromAuthorOrTopic(subscribeType, subscribedItem.id);

        if (response.status === 'ok') {
          setSubscribedItems(subscribedItems?.filter((item) => item.id !== id) || null);

          setNonSubscribedItems([...(nonSubscribedItems || []), subscribedItem]);

          openSnackbar(getSnackbarMessage('unsubscribe'));
        } else {
          throw new Error();
        }
      }
    } catch (error) {
      if (item) {
        openSnackbar(getSnackbarMessage('failedSubscribe'));
      } else {
        openSnackbar(getSnackbarMessage('failedUnsubscribe'));
      }

      console.error(`Error ${item ? 'subscribing to' : 'unsubscribing from'} ${type}:`, error);
    }
  };

  const onFollowClick = (id: number, type: 'author' | 'topic') => {
    const userAction = type === 'author' ? 'follow-author' : 'follow-topic';

    // check if user user is logged in. If not, set pending action details in session storage and exit
    if (!handleUserActionWithLoginCheck(userAction, id)) {
      return;
    }

    handleFollow(id, type);
  };

  const swiperRef = useRef<any>(null);

  // If the data is still loading, display white space with hard-coded height
  if (isLoading) {
    return (
      <main
        className="page-newsletter__section--regular page-newsletter__section--carousel"
        style={{ height: '420px' }}
      >
        <div className="container" />
      </main>
    );
  }

  return (
    <div className="page-newsletter__section--regular page-newsletter__section--carousel">
      <SectionHeader
        title={
          cardType === 'author'
            ? translations?.newsletterPage?.authorsBlockTitle || 'Follow authors you like'
            : translations?.newsletterPage?.topicsBlockTitle || 'Follow topics you like'
        }
      />

      <p className="page-newsletter__section-description page-newsletter__section-description--carousel basic-16">
        {cardType === 'author'
          ? translations?.newsletterPage?.authorsBlockSubtitle || 'Follow authors you like'
          : translations?.newsletterPage?.topicsBlockSubtitle || 'Follow topics you like'}
      </p>

      <div className="react-block__carousel react-block__carousel--newsletters">
        <div className={`carousel-button ${cardType}s__button--prev`}>
          <Icon name="arrow-left" size="small" />
        </div>

        <div className={`carousel-button ${cardType}s__button--next`}>
          <Icon name="arrow-right" size="small" />
        </div>

        <Swiper
          ref={swiperRef}
          modules={[Navigation, Pagination, Mousewheel, Keyboard]}
          className="react-block__swiper"
          spaceBetween={30}
          slidesPerView="auto"
          navigation={{
            // use unique class names for each carousel on the page to avoid conflicts on button clicks
            nextEl: `.${cardType}s__button--next`,
            prevEl: `.${cardType}s__button--prev`,
          }}
          scrollbar={false}
          breakpoints={{
            320: { slidesPerView: 1.2 },
            768: { slidesPerView: 2.2 },
            991: { slidesPerView: 3.2 },
            1200: { slidesPerView: 4.2 },
          }}
        >
          {allItems?.map((item) => (
            <SwiperSlide key={item.id}>
              <AuthorAndTopicCard
                key={item.id}
                title={item.name}
                id={item.id.toString()}
                type={cardType}
                link={item.link}
                imageUrl={(item.acf?.newsletter_image || item?.acf?.profileImageUrl) ?? ''}
                shortDescription={item?.acf?.shortDescription ?? ''}
                description={item?.acf?.newsletter_description || item?.description || ''}
                isFollowed={subscribedItems?.includes(item) ?? false}
                onFollowClick={(id: number, type: 'author' | 'topic') => onFollowClick(id, type)}
                layout={'carousel'}
                isFollowable={true}
                nameType="link"
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default NewsletterCarousel;
